import React, { useState, useMemo } from 'react';
import {
  Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, Grid, Card, CardContent, Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import { ColumnDef } from '../../utils/exportUtils';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';

const STATUS_OPTIONS = ['', 'proposed', 'approved', 'in_transit', 'delivered', 'rejected', 'cancelled'];

const statusColor: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  proposed: 'default',
  approved: 'info',
  in_transit: 'warning',
  delivered: 'success',
  rejected: 'error',
  cancelled: 'error',
};

function defaultDateFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

const TransferHistoryTab: React.FC = () => {
  const { fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('');

  const { data: transfers = [], isLoading, error } = useQuery({
    queryKey: ['transferHistory', dateFrom, dateTo, statusFilter],
    queryFn: async () => {
      const params: any = { limit: 500 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter) params.status = statusFilter;
      const res = await api.getTransferHistory(params);
      return res.data;
    },
  });

  const kpis = useMemo(() => {
    const total = transfers.length;
    const inTransit = transfers.filter((t: any) => t.status === 'in_transit').length;
    const delivered = transfers.filter((t: any) => t.status === 'delivered').length;
    const rejected = transfers.filter((t: any) => t.status === 'rejected').length;
    const claims = transfers.filter((t: any) => t.has_claim).length;
    return { total, inTransit, delivered, rejected, claims };
  }, [transfers]);

  const exportColumns: ColumnDef[] = [
    { key: 'to_number', header: 'TO Number' },
    { key: 'proposed_date', header: 'Proposed Date' },
    { key: 'shipped_date', header: 'Shipped Date' },
    { key: 'delivered_date', header: 'Delivered Date' },
    { key: 'from_store_name', header: 'From Store' },
    { key: 'to_store_name', header: 'To Store' },
    { key: 'product_name', header: 'Product' },
    { key: 'product_sku', header: 'SKU' },
    { key: 'lifecycle_stage', header: 'Item Status' },
    { key: 'recommended_qty', header: 'Qty Recommended' },
    { key: 'approved_qty', header: 'Qty Approved' },
    { key: 'actual_qty', header: 'Qty Actual' },
    { key: 'value', header: `Value ${fmtCurrencyLabel()}`, format: (v: number) => fmtCurrency(v) },
    { key: 'status', header: 'Transfer Status' },
    { key: 'claim_reason', header: 'Claim' },
  ];

  const formatDate = (val: string | null) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString();
  };

  const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <FullscreenWrap>
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
      </Box>
      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Transfers', value: kpis.total, color: '#1976d2' },
          { label: 'In Transit', value: kpis.inTransit, color: '#ed6c02' },
          { label: 'Delivered', value: kpis.delivered, color: '#2e7d32' },
          { label: 'Rejected', value: kpis.rejected, color: '#d32f2f' },
          { label: 'Claims', value: kpis.claims, color: kpis.claims > 0 ? '#d32f2f' : '#757575' },
        ].map((kpi) => (
          <Grid item xs={12} sm={6} md key={kpi.label}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h5" sx={{ color: kpi.color, fontWeight: 600 }}>
                  {kpi.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="From" type="date" size="small" value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To" type="date" size="small" value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Transfer Status</InputLabel>
          <Select value={statusFilter} label="Transfer Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.filter(Boolean).map((s) => (
              <MenuItem key={s} value={s}>{formatStatus(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <ExportMenu
            data={transfers}
            columns={exportColumns}
            filename="transfer-history"
            sheetName="Transfer History"
            title="Transfer History Report"
          />
        </Box>
      </Box>

      {/* Table */}
      {isLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load transfer history</Alert>}

      {!isLoading && !error && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>TO Number</TableCell>
                <TableCell>Proposed Date</TableCell>
                <TableCell>Shipped Date</TableCell>
                <TableCell>Delivered Date</TableCell>
                <TableCell>From Store</TableCell>
                <TableCell>To Store</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Item Status</TableCell>
                <TableCell align="right">Qty Rec.</TableCell>
                <TableCell align="right">Qty Appr.</TableCell>
                <TableCell align="right">Qty Actual</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell>Transfer Status</TableCell>
                <TableCell>Claim</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>No transfers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers.map((t: any) => (
                  <TableRow key={t.transfer_id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {t.to_number || '—'}
                    </TableCell>
                    <TableCell>{formatDate(t.proposed_date)}</TableCell>
                    <TableCell>{formatDate(t.shipped_date)}</TableCell>
                    <TableCell>{formatDate(t.delivered_date)}</TableCell>
                    <TableCell>{t.from_store_name}</TableCell>
                    <TableCell>{t.to_store_name}</TableCell>
                    <TableCell>{t.product_name}</TableCell>
                    <TableCell>{t.product_sku}</TableCell>
                    <TableCell sx={{ py: '6px', px: '8px' }}>
                      {t.lifecycle_stage ? (
                        <Chip
                          label={t.lifecycle_stage}
                          size="small"
                          color={
                            t.lifecycle_stage === 'NEW' ? 'info' :
                            t.lifecycle_stage === 'GROWTH' ? 'success' :
                            t.lifecycle_stage === 'DECLINING' ? 'warning' :
                            (t.lifecycle_stage === 'PHASE_OUT' || t.lifecycle_stage === 'DISCONTINUED') ? 'error' :
                            'default'
                          }
                          variant="outlined"
                          sx={{ fontSize: '10px', height: 20 }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{t.recommended_qty}</TableCell>
                    <TableCell align="right">{t.approved_qty ?? '—'}</TableCell>
                    <TableCell align="right">{t.actual_qty ?? '—'}</TableCell>
                    <TableCell align="right">{fmtCurrency(t.value)}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatus(t.status)}
                        size="small"
                        color={statusColor[t.status] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {t.has_claim && (
                        <Tooltip title={t.claim_reason || 'Claim'} arrow>
                          <WarningAmberIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
    </FullscreenWrap>
  );
};

export default TransferHistoryTab;
